import {
  DocumentPdfPane,
  DocumentViewer,
  type DocumentPaneRenderProps,
  type DocumentViewerHighlight,
} from '@aecom-ai-engineering/react-components';
import { Spinner } from '@consigli/facade';
import { useGetBidComparisonQuery, useGetBlobReadUrlQuery } from '@consigli/hooks';
import { type BidComparisonResponse } from '@consigli/types';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { HiInformationCircle } from 'react-icons/hi';
import { HiSparkles } from 'react-icons/hi2';

import { BidComparisonTable } from './bid-comparison-table';

type BidComparisonRow = BidComparisonResponse['rows'][number];
type BidComparisonCell = BidComparisonRow['cells'][number];
type BidComparisonCitation = NonNullable<BidComparisonCell['citation']>;

const EMPTY_DATA: BidComparisonResponse = {
  summary: '',
  bidsCompared: 0,
  itemsLeveled: 0,
  totalWeighting: 0,
  contractors: [],
  rows: [],
};

interface ActiveSource {
  citation: BidComparisonCitation;
  heading: string;
  valueLabel: string;
  value: string;
}

const StatCard: FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className="flex flex-1 flex-col gap-1 rounded-md border border-gray-200 bg-white px-4 py-3">
    <span className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</span>
    <span className="text-2xl font-semibold text-gray-900">{value}</span>
  </div>
);

interface SourcePanelProps {
  citation: BidComparisonCitation;
  heading: string;
  valueLabel: string;
  value: string;
  projectId: number;
  packageId: string;
  onClose: () => void;
}

const SourcePanel: FC<SourcePanelProps> = ({
  citation,
  heading,
  valueLabel,
  value,
  projectId,
  packageId,
  onClose,
}) => {
  const { data, isError } = useGetBlobReadUrlQuery({
    projectId,
    packageId: citation.documentPackageId ?? packageId,
    blobId: citation.blobId,
  });

  const highlights = useMemo((): DocumentViewerHighlight[] | undefined => {
    if (citation.x0 == null || citation.y0 == null || citation.x1 == null || citation.y1 == null) {
      return undefined;
    }
    return [
      {
        x0: citation.x0,
        y0: citation.y0,
        x1: citation.x1,
        y1: citation.y1,
        pageNumber: citation.page,
      },
    ];
  }, [citation]);

  const renderers = useMemo(
    () => ({
      pdf: (props: DocumentPaneRenderProps) => (
        <DocumentPdfPane
          active={props.active}
          caption={props.caption}
          onSidebarContextChange={props.onSidebarContextChange}
          src={props.src}
          initialPage={citation.page}
          highlights={highlights}
          continuousScroll
        />
      ),
    }),
    [citation.page, highlights],
  );

  const src = data?.readUrl;

  if (!src) {
    if (isError) return null;
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/20"
        role="presentation"
        onClick={onClose}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <DocumentViewer
      open
      onOpenChange={(details) => {
        if (!details.open) onClose();
      }}
      src={src}
      title={citation.label ?? heading}
      headerLabel={heading}
      showSidebar
      sidebarLabel="Source details"
      renderers={renderers}
    >
      <div className="flex flex-col gap-4 p-4 text-sm">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium uppercase tracking-wider text-gray-500">
            {heading}
          </span>
          <span className="text-xs text-gray-500">page {citation.page}</span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            {valueLabel}
          </span>
          <span className="whitespace-pre-line text-base font-semibold text-gray-900">{value}</span>
        </div>
        {citation.passage && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Extract
            </span>
            <span className="whitespace-pre-line text-sm text-gray-700">{citation.passage}</span>
          </div>
        )}
      </div>
    </DocumentViewer>
  );
};

interface BidComparisonTabProps {
  projectId: number;
  packageId: string;
}

export const BidComparisonTab: FC<BidComparisonTabProps> = ({ projectId, packageId }) => {
  const { data: responseData, isLoading } = useGetBidComparisonQuery({ projectId, packageId });
  const data = responseData ?? EMPTY_DATA;

  const [weights, setWeights] = useState<Record<string, number>>({});
  const [activeSource, setActiveSource] = useState<ActiveSource | null>(null);
  const [activeRowId, setActiveRowId] = useState<string | null>(null);

  useEffect(() => {
    setWeights(Object.fromEntries(data.rows.map((row) => [row.id, row.weight])));
  }, [data.rows]);

  const handleWeightChange = useCallback((rowId: string, value: string) => {
    const parsed = Number(value);
    setWeights((current) => ({
      ...current,
      [rowId]: Number.isFinite(parsed) ? parsed : 0,
    }));
  }, []);

  const handleViewSource = useCallback(
    (citation: BidComparisonCitation, cell: BidComparisonCell, rowId: string) => {
      const contractorName =
        data.contractors.find((contractor) => contractor.id === cell.contractorId)?.name ??
        cell.contractorId;
      setActiveRowId(rowId);
      setActiveSource({
        citation,
        heading: contractorName,
        valueLabel: 'Finding',
        value: cell.value,
      });
    },
    [data.contractors],
  );

  const handleViewReference = useCallback((row: BidComparisonRow) => {
    if (!row.reference) return;
    setActiveRowId(row.id);
    setActiveSource({
      citation: row.reference,
      heading: 'Tender',
      valueLabel: 'Requirement',
      value: row.item,
    });
  }, []);

  const handleCloseSource = useCallback(() => {
    setActiveSource(null);
    setActiveRowId(null);
  }, []);

  const summaryStats = useMemo(
    () => [
      { label: 'Bids compared', value: data.bidsCompared },
      { label: 'Items leveled', value: data.itemsLeveled },
      { label: 'Total weighting', value: `${data.totalWeighting}%` },
    ],
    [data.bidsCompared, data.itemsLeveled, data.totalWeighting],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-gray-600" />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex items-start gap-2 rounded-md border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-700">
        <HiInformationCircle className="mt-0.5 shrink-0 text-gray-500" size={16} />
        <span>
          This comparison is AI-assisted and shown for preview. Verify figures against the source
          documents before making commercial decisions.
        </span>
      </div>

      <div className="flex flex-col gap-4 rounded-md border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2">
          <HiSparkles className="text-gray-700" size={18} />
          <h2 className="text-base font-semibold text-gray-900">AI Summary</h2>
        </div>
        {data.summary && <p className="text-sm leading-relaxed text-gray-700">{data.summary}</p>}
        <div className="flex flex-col gap-4 sm:flex-row">
          {summaryStats.map((stat) => (
            <StatCard key={stat.label} label={stat.label} value={stat.value} />
          ))}
        </div>
      </div>

      {data.rows.length === 0 ? (
        <div className="flex items-center justify-center rounded-md border border-gray-200 bg-white px-4 py-10 text-sm text-gray-500 shadow-sm">
          No bid comparison data is available yet for this package.
        </div>
      ) : (
        <BidComparisonTable
          data={data}
          weights={weights}
          activeRowId={activeRowId}
          onWeightChange={handleWeightChange}
          onViewSource={handleViewSource}
          onViewReference={handleViewReference}
        />
      )}

      {activeSource && (
        <SourcePanel
          citation={activeSource.citation}
          heading={activeSource.heading}
          valueLabel={activeSource.valueLabel}
          value={activeSource.value}
          projectId={projectId}
          packageId={packageId}
          onClose={handleCloseSource}
        />
      )}
    </div>
  );
};
